import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import * as Icons from 'lucide-react';

interface ProForm {
  id: string;
  route_path: string;
  form_name: string;
  form_description: string | null;
  request_type: string;
  credit_cost: number;
  example_media_url: string | null;
  example_media_type: 'image' | 'video' | 'audio' | null;
}

export const ProQuestPage = () => {
  const navigate = useNavigate();
  const [proForms, setProForms] = useState<ProForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActiveForms = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('pro_forms')
          .select('id, route_path, form_name, form_description, request_type, credit_cost, example_media_url, example_media_type')
          .order('created_at', { ascending: false });

        if (fetchError) {
          throw fetchError;
        }

        setProForms(data || []);
      } catch (err) {
        console.error('Error fetching pro forms:', err);
        setError(err instanceof Error ? err.message : 'Failed to load pro forms');
      } finally {
        setLoading(false);
      }
    };

    fetchActiveForms();
  }, []);

  const handleFormClick = (routePath: string) => {
    // Remove leading slash if present to avoid double slashes
    const cleanPath = routePath.startsWith('/') ? routePath.slice(1) : routePath;

    const targetPath = `/pro/${cleanPath}`;
    navigate(targetPath);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="text-th-secondary mt-4">Loading Pro Request forms...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md bg-elevated border border-subtle rounded-lg p-8">
          <p className="text-status-error mb-4">Error loading forms: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="button-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto p-6 pb-20">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-th-primary mb-2">Pro Request Forms</h1>
          <p className="text-th-secondary">
            Browse and submit custom AI generation requests handled by our professional team
          </p>
        </div>

        {proForms.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-elevated border border-subtle mb-4">
              <Icons.FileText className="w-8 h-8 text-th-secondary" />
            </div>
            <h3 className="text-xl font-semibold text-th-primary mb-2">
              No Pro Forms Available
            </h3>
            <p className="text-th-secondary">
              There are currently no active pro request forms. Check back later!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {proForms.map((form) => (
              <div
                key={form.id}
                onClick={() => handleFormClick(form.route_path)}
                className="bg-elevated border border-subtle rounded-lg overflow-hidden hover:border-brand transition-all duration-200 cursor-pointer group"
              >
                {form.example_media_url && (
                  <div className="relative w-full h-48 bg-base overflow-hidden">
                    {form.example_media_type === 'video' ? (
                      <video
                        src={form.example_media_url}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        muted
                        loop
                        playsInline
                        onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
                        onMouseLeave={(e) => (e.target as HTMLVideoElement).pause()}
                      />
                    ) : form.example_media_type === 'audio' ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icons.Music className="w-16 h-16 text-brand" />
                      </div>
                    ) : (
                      <img
                        src={form.example_media_url}
                        alt={form.form_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-th-primary group-hover:text-brand transition-colors">
                      {form.form_name}
                    </h3>
                    <div className="flex items-center gap-1 text-accent bg-accent/10 px-2 py-1 rounded text-sm font-medium">
                      <Icons.Zap className="w-3 h-3" />
                      {form.credit_cost}
                    </div>
                  </div>

                  {form.form_description && (
                    <p className="text-th-secondary text-sm mb-4 line-clamp-2">
                      {form.form_description}
                    </p>
                  )}

                  <div className="flex items-center gap-2 text-xs text-th-secondary">
                    <span className="px-2 py-1 bg-base rounded">
                      {form.request_type}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
